import { Controller } from "@hotwired/stimulus";

/** Ключ истории запросов в localStorage. */
const RECENT_KEY = "biblion:search-recent";
const RECENT_LIMIT = 5;

/**
 * Поиск по сайту — спотлайт поверх страницы.
 *
 * Подсказки приходят готовым HTML от обычной Django-вьюхи, контроллеру остаётся
 * вставить ответ. Прежняя версия держала поле ввода прямо в шапке, а выпадашку —
 * внутри него: панель обрезалась контейнером и занимала место в узкой шапке.
 */
export default class extends Controller {
  static targets = ["input", "panel", "spotlight", "recent", "recentList", "chipTemplate", "busy"];
  static values = {
    url: String,
    minLength: { type: Number, default: 3 },
    delay: { type: Number, default: 300 },
  };

  disconnect() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.setBusy(false);
    document.body.classList.remove("u-no-scroll");
  }

  open(event) {
    event?.preventDefault();

    this.spotlightTarget.hidden = false;
    // Фон не должен уезжать под панелью.
    document.body.classList.add("u-no-scroll");
    this.renderRecent();
    this.inputTarget.focus();
    this.inputTarget.select();
  }

  close() {
    if (this.spotlightTarget.hidden) return;

    this.spotlightTarget.hidden = true;
    document.body.classList.remove("u-no-scroll");
    this.request?.abort();
    this.setBusy(false);
    clearTimeout(this.timer);
  }

  /** Клик по затемнению, но не по самой панели. */
  closeOnBackdrop(event) {
    if (event.target === this.spotlightTarget) this.close();
  }

  /** Ввод: ждём паузы в наборе, чтобы не слать запрос на каждую букву. */
  search() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.fetchSuggestions(), this.delayValue);
  }

  /** Клик по подсказке: запрос пригодился, кладём его в историю. */
  remember(event) {
    if (!event.target.closest("a")) return;
    this.saveRecent(this.inputTarget.value.trim());
  }

  /** Клик по чипу недавнего запроса: подставляем и ищем сразу. */
  useRecent(event) {
    this.inputTarget.value = event.currentTarget.textContent.trim();
    this.inputTarget.focus();
    this.fetchSuggestions();
  }

  async fetchSuggestions() {
    const query = this.inputTarget.value.trim();
    if (query.length < this.minLengthValue) {
      this.panelTarget.replaceChildren();
      this.setBusy(false);
      return;
    }

    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    this.setBusy(true);

    try {
      const response = await fetch(
        `${this.urlValue}?q=${encodeURIComponent(query)}`,
        { signal: request.signal, headers: { Accept: "text/html" } },
      );
      if (!response.ok) throw new Error(String(response.status));

      this.panelTarget.innerHTML = await response.text();
    } catch (error) {
      // Прерванный запрос — это следующая буква в поле: спиннер оставляем,
      // его снимет запрос, который придёт последним.
      if (error.name === "AbortError") return;
      this.panelTarget.replaceChildren();
    } finally {
      // Пришёл ответ на последний запрос — только тогда гасим спиннер.
      if (this.request === request) this.setBusy(false);
    }
  }

  /** Спиннер рядом с полем: запрос идёт. */
  setBusy(busy) {
    if (this.hasBusyTarget) this.busyTarget.hidden = !busy;
  }

  /** Недавние запросы. Хранилище может быть недоступно — тогда просто без них. */
  readRecent() {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY));
      return Array.isArray(stored) ? stored.filter((item) => typeof item === "string") : [];
    } catch {
      return [];
    }
  }

  saveRecent(query) {
    if (query.length < this.minLengthValue) return;

    const queries = [query, ...this.readRecent().filter((item) => item !== query)].slice(0, RECENT_LIMIT);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(queries));
    } catch {
      // Приватный режим или запрет на хранилище: историю просто не ведём.
    }
  }

  renderRecent() {
    if (!this.hasRecentTarget) return;

    const queries = this.readRecent();
    this.recentTarget.hidden = queries.length === 0;

    const chips = queries.map((query) => {
      const chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true);
      chip.textContent = query;
      return chip;
    });
    this.recentListTarget.replaceChildren(...chips);
  }
}
